import { Button, DropDownButton, DropDownButtonItem } from "@progress/kendo-react-buttons";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import { AutoComplete, AutoCompleteChangeEvent, ListItemProps } from "@progress/kendo-react-dropdowns";
import { Checkbox, RadioButton } from "@progress/kendo-react-inputs";
import { PayLineItem } from "TypeGen/Order/Planning/Vehicle/pay-line-item";
import { DispatchStatusType } from "TypeGen/Order/dispatch-status-type";
import { CalculatePayRateResult } from "TypeGen/Pay/calculate-pay-rate-result";
import { JsonResponse } from "TypeGen/json-response";
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useDebouncedCallback } from "use-debounce";
import { PlanningContext } from ".";
import { BorderCrossingResult } from "../../../TypeGen/Order/Planning/border-crossing-result";
import LoadingPanel from "../../../components/LoadingPanel";
import { fetchApi } from "../../../services/api";
import { ISingleSearchLocation, singleSearch } from "../../../services/pcmiler";
import { ILink } from "../../../types/link";
import BorderCrossingReferenceNumberInputs from "./BorderCrossingReferenceNumberInputs";
import PayEditable from "./PayEditable";

type OrderPlanningVehicleDetails = {
  VehicleNumber: string;
  VehicleMPH: number;
  AvailableNote: string;
  ShipDateTime: Date;
  DispatchStatus: DispatchStatusType;
  DeadHeadStartDateTime: Date;
  LocationOptions: LocationOption[];
  Pay: CalculatePayRateResult;
  ExistingPay: CalculatePayRateResult | null;
  DriverID1: number;
  BypassLoadConfirmation: boolean;

  Errors: string[];
  Warnings: string[];

  Links: ILink[];
}

type LocationOption = {
  // For Display Only
  Label: string;
  Name: string;
  DeadHeadMiles: number;

  // Selected
  CityID?: number;
  CustomerID?: number;

  // Manually Entered
  City: string;
  State: string;
  Zip: string;
}

type RouteComponentParams = {
  orderId: string;
  vehicleId: string;
};

const OrderPlanningVehicle = () => {
  const history = useHistory();
  const { borderCrossingResult } = useContext(PlanningContext);
  const { orderId: orderIdParam, vehicleId: vehicleIdParam } = useParams<RouteComponentParams>();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<OrderPlanningVehicleDetails>();

  // Location AutoComplete
  const locationInputRef = useRef<AutoComplete>(null);
  const [locationOptions, setLocationOptions] = useState<ISingleSearchLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ISingleSearchLocation>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationValue, setLocationValue] = useState('');

  // Options
  const [locationOption, setLocationOption] = useState<LocationOption>();
  const [deadHeadStartDateTime, setDeadHeadStartDateTime] = useState<Date>();
  const [useExistingPay, setUseExistingPay] = useState<"true" | "false">("false");
  const [bypassLoadConfirmation, setBypassLoadConfirmation] = useState(false);
  const [borderReferenceNumber, setBorderReferenceNumber] = useState<BorderCrossingResult>(borderCrossingResult ?? {} as BorderCrossingResult);

  const refreshOrderDetails = useCallback(() => {
    setLoading(true);
    fetchApi(`/api/Order/PlanningDetailsVehicle/${orderIdParam}/${vehicleIdParam}`)
      .then((response: OrderPlanningVehicleDetails) => {
        setLocationOption(response.LocationOptions[0]);
        setDeadHeadStartDateTime(dayjs.utc(response.DeadHeadStartDateTime).toDate());
        setDetails(response);
        if (response.ExistingPay) setUseExistingPay("true");
        setBypassLoadConfirmation(response.BypassLoadConfirmation);
        setLoading(false);
      })
      .catch(err => {
        alert(err);
        setLoading(false);
      });
  }, [orderIdParam, vehicleIdParam]);

  const calculatePayRate = (deadHeadMiles: number, Coords: { Lat: string, Lon: string } = null) => {
    setLoading(true);
    const data = {
      VehicleID: vehicleIdParam,
      DriverID: details.DriverID1,
      DispatchOrderID: orderIdParam,
      DeadHeadMiles: deadHeadMiles,
      DeadheadStartLatitude: Coords?.Lat,
      DeadheadStartLongitude: Coords?.Lon
    };
    fetchApi(`/api/Settlements/CalculatePayRate`, data, 'POST')
      .then((response: CalculatePayRateResult) => {
        const existingIndex = details.Pay.LineItems.findIndex(item => item.RateDescriptionID == 600);
        if (response.LineItems[0].RateDescriptionID == 600 || existingIndex !== -1) {
          if (response.LineItems[0].RateDescriptionID == 600) {
            if (existingIndex !== -1) {
              details.Pay.LineItems[existingIndex] = response.LineItems[0];
            } else {
              details.Pay.LineItems.unshift({ ...response.LineItems[0] });
            }
          } else if (existingIndex !== -1) {
            details.Pay.LineItems.splice(existingIndex, 1);
          }
          details.Pay.Total = details.Pay.LineItems.reduce((accumulator, item) => accumulator + item.Amount, 0);
          setDetails({ ...details });
        }
        setLoading(false);
      })
      .catch(err => {
        alert(err);
        setLoading(false);
      });
  }

  const calculateEtaWarning = useCallback(() => {

    if (!details) return '';

    const eta = dayjs.utc(deadHeadStartDateTime).add(locationOption.DeadHeadMiles / details.VehicleMPH, 'hours');
    const schedule = dayjs.utc(details.ShipDateTime);

    return eta.isAfter(schedule) ? `Calculated ETA to first pick-up (${eta.tz('America/New_York').format("MM/DD/YYYY HH:mm")}) is later than scheduled pick-up date/time (${schedule.tz('America/New_York').format("MM/DD/YYYY HH:mm")})` : '';
  }, [details, locationOption, deadHeadStartDateTime]);

  const dispatchOrder = (dispatchStatus: DispatchStatusType) => {
    setLoading(true);

    const payload = {
      DispatchStatus: dispatchStatus,
      Location: locationOption,
      DeadHeadStartDateTime: deadHeadStartDateTime,
      PARSNumber: borderReferenceNumber?.PARS || '',
      PAPSNumber: borderReferenceNumber?.PAPS || '',
      // Sent on planning too: a planned load is auto-dispatched later once the driver empties,
      // and the backend stores this on the order so the choice is still there when that happens.
      BypassLoadConfirmation: bypassLoadConfirmation,
      PayLineItems: (useExistingPay == 'false' || details.ExistingPay == null) ? details.Pay.LineItems.map(x => {
        return {
          RateDescriptionID: x.RateDescriptionID,
          Description: x.Description,
          Rate: x.Rate,
          RateUoM: x.RateUoM,
          Quantity: x.Quantity,
          QuantityUoM: x.QuantityUoM
        } as PayLineItem
      }
      ) : undefined
    };

    const command = details.Links.find(x => x.Name === 'Dispatch' || x.Name === 'Plan');
    fetchApi(command.Link, payload, command.Method)
      .then((response: JsonResponse) => {
        if (response.Success) {
          history.push(response.Links.find(x => x.Name === "Track").Link);
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch(e => {
        // If not problem details
        if (!e?.status) alert('Unable to dispatch');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const unPlanOrder = () => {
    setLoading(true);

    const command = details.Links.find(x => x.Name === 'UnPlan');
    fetchApi(command.Link, {}, command.Method)
      .then((response: JsonResponse) => {
        if (response.Success) {
          history.push(response.Links.find(x => x.Name === "Track").Link);
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch(e => {
        // If not problem details
        if (!e?.status) alert('Unable to un-plan');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const renderLocation = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    const itemChildren = <span>{itemProps.dataItem.ShortString}</span>;
    return React.cloneElement(li, li.props, itemChildren);
  }

  const updateLocationsDebounced = useDebouncedCallback((searchTerm: string) => {
    setLocationLoading(true);
    singleSearch(searchTerm)
      .then((response) => {
        setLocationLoading(false);
        setLocationOptions(response.Locations);
      })
      .catch(() => {
        setLocationLoading(false);
        setLocationOptions([]);
      })
  }, 350);

  const etaWarning = calculateEtaWarning();

  useEffect(() => {
    refreshOrderDetails();
  }, [orderIdParam, vehicleIdParam, refreshOrderDetails]);

  if (loading && !details) {
    return <LoadingPanel />;
  }

  return <div className="card my-2">
    <div className="card-body">
      <h5 className="card-title text-center">Plan Vehicle # {details.VehicleNumber}</h5>
      {details.Errors.map((error, index) => <div className="alert alert-danger" key={index}>
        {error}
      </div>)}
      {details.Warnings.map((warning, index) => <div className="alert alert-warning" key={index}>
        {warning}
      </div>)}
      {etaWarning && <div className="alert alert-warning">
        {etaWarning}
      </div>}
      {details.AvailableNote && <div className="alert alert-dark" role="alert">
        {details.AvailableNote}
      </div>}
      {details.LocationOptions.map((x, index) => <div className="form-group row" key={index}>
        <label className="col-form-label form-check-label col-md-3" htmlFor={`location${index}`}>{x.Label}</label>
        <div className="col-md-9 mt-1 pl-4">
          <input
            className="form-check-input"
            type="radio"
            name="locations"
            id={`location${index}`}
            defaultChecked={index === 0}
            onChange={() => {
              setLocationOption(x);
              calculatePayRate(x.DeadHeadMiles);
            }}
          />
          <span className="align-middle">
            {x.Name}
          </span>
        </div>
      </div>)}
      <div className="form-group row">
        <label className="col-form-label form-check-label col-md-3" htmlFor="location">Another City, State</label>
        <div className="col-md-9 mt-1 pl-4">
          <input
            className="form-check-input"
            type="radio"
            name="locations"
            id="location"
            onChange={(e) => {
              if (selectedLocation) {
                setLocationOption({
                  Name: '',
                  Label: 'Another City, State',
                  DeadHeadMiles: 0,
                  City: selectedLocation.Address.City,
                  State: selectedLocation.Address.State,
                  Zip: selectedLocation.Address.Zip,
                });
                calculatePayRate(0, selectedLocation.Coords);
              } else {
                e.preventDefault();
                locationInputRef.current?.focus();
              }
            }}
            checked={locationOption?.Label === "Another City, State"}
          />
          <AutoComplete
            ref={locationInputRef}
            data={locationOptions}
            value={locationValue}
            loading={locationLoading}
            placeholder={`Enter Location`}
            itemRender={renderLocation}
            onChange={(event: AutoCompleteChangeEvent) => {

              // Update Location
              let location = event.target.value;
              if (typeof event.target.value === 'object') {
                const place = event.target.value as ISingleSearchLocation;
                location = place.Address.Zip + ' ' + place.Address.City + ', ' + place.Address.State;
                setLocationOption({
                  Name: '',
                  Label: 'Another City, State',
                  DeadHeadMiles: 0,
                  City: place.Address.City,
                  State: place.Address.State,
                  Zip: place.Address.Zip,
                });

                calculatePayRate(0, place.Coords);

                // This value is stored so they can select a different option and this option again
                setSelectedLocation(place);
              } else if (!event.target.value) {
                // Clear Selected Location
                setSelectedLocation(null);
              }

              setLocationValue(location);

              // Update Autocomplete Suggestions
              if (typeof event.target.value !== 'object' && event.target.value.length > 2) {
                updateLocationsDebounced(event.target.value);
              }
            }}
          />
        </div>
      </div>
      <div className="form-group row">
        <label htmlFor="deadHeadStartTime" className="col-form-label col-md-3">Dead Head Start Time</label>
        <div className="col-md-9">
          <DateTimePicker
            required
            width={210}
            format="MM/dd/yyyy HH:mm"
            id="deadHeadStartTime"
            value={deadHeadStartDateTime}
            onChange={(e) => setDeadHeadStartDateTime(e.value)}
          />
        </div>
      </div>
      <BorderCrossingReferenceNumberInputs
        borderReference={borderReferenceNumber}
        onParsChange={(value) => {
          setBorderReferenceNumber(prev => ({
            ...(prev ?? ({} as BorderCrossingResult)),
            PARS: value
          }));
        }}
        onPapsChange={(value) => {
          setBorderReferenceNumber(prev => ({
            ...(prev ?? ({} as BorderCrossingResult)),
            PAPS: value
          }));
        }}
      />
      {details.ExistingPay && <>
        <RadioButton
          className="my-2"
          name="existingPay"
          value="true"
          checked={useExistingPay === "true"}
          label="Use Existing Pay"
          onChange={(e) => setUseExistingPay(e.value)}
        />
        <PayEditable
          editable={false}
          data={details.ExistingPay}
        />
        <RadioButton
          className="my-2"
          name="existingPay"
          value="false"
          checked={useExistingPay === "false"}
          label="Set New Pay"
          onChange={(e) => setUseExistingPay(e.value)}
        />
      </>}
      <PayEditable
        data={details.Pay}
        onChange={(pay) => {
          setDetails({ ...details, Pay: pay });
          setUseExistingPay("false");
        }}
      />
      {details.Links.find(x => x.Name === 'Dispatch' || x.Name === 'Plan') && <div className="text-center mt-3">
        <Checkbox
          value={bypassLoadConfirmation}
          onChange={(e) => setBypassLoadConfirmation(e.value)}
          label="Driver already confirmed, skip app notification and confirmation call"
        />
        {/* Planning stores the choice rather than acting on it, and the load is not dispatched
            until the driver empties, so say when it will actually take effect. */}
        {details.Links.find(x => x.Name === 'Dispatch') == null && bypassLoadConfirmation && <div
          className="text-muted mt-1"
          style={{ fontSize: '0.8rem' }}
        >Applies when this load is auto-dispatched.</div>}
      </div>}
      <div className="text-center mt-3">
        <Button themeColor="secondary" onClick={() => history.push(`/Order/Planning/${orderIdParam}`)}>
          Back
        </Button>
        {details.Links.find(x => x.Name === 'UnPlan') && <Button
          themeColor="error"
          className="ml-2"
          onClick={unPlanOrder}
          disabled={loading}
        >
          Un-Plan Order
        </Button>}
        {details.Links.find(x => x.Name === 'Plan') && <DropDownButton
          themeColor="primary"
          buttonClass="ml-2"
          disabled={details.Errors.length > 0 || (useExistingPay === "false" && details.Pay.Total === 0) || loading}
          onItemClick={(e) => dispatchOrder(e.itemIndex === 0 ? DispatchStatusType.PlannedReady : DispatchStatusType.PlannedOnHold)}
          text="Plan Order"
        >
          <DropDownButtonItem text="Auto Dispatch" />
          <DropDownButtonItem text="On-Hold Dispatch" />
        </DropDownButton>}
        {details.Links.find(x => x.Name === 'Dispatch') && <Button
          themeColor="success"
          className="ml-2"
          disabled={details.Errors.length > 0 || (useExistingPay === "false" && details.Pay.Total === 0) || loading}
          onClick={() => dispatchOrder(DispatchStatusType.Started)}
        >
          Dispatch Order
        </Button>}
      </div>
    </div>
  </div>
}

export default OrderPlanningVehicle;